import { useEffect, useState } from "react";
import { X, Cookie, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { readConsent, saveConsent, applyConsent } from "@/lib/consent";

const T = {
  fr: {
    title: "Nous respectons votre vie privée",
    intro:
      "Nous utilisons uniquement les cookies strictement nécessaires par défaut. Avec votre accord, nous pouvons mesurer l'audience du site et diffuser des publicités pertinentes. Vous pouvez modifier vos choix à tout moment.",
    learnMore: "Lire la politique de confidentialité",
    accept: "Tout accepter",
    reject: "Tout refuser",
    customize: "Personnaliser",
    savePrefs: "Enregistrer mes choix",
    close: "Fermer",
    cats: {
      necessary: { name: "Strictement nécessaires", desc: "Sécurité (Cloudflare), session et authentification. Ces cookies sont indispensables au bon fonctionnement du site.", locked: "Toujours actifs" },
      analytics: { name: "Mesure d'audience", desc: "Google Analytics et PostHog. Nous aident à comprendre l'utilisation du site pour l'améliorer." },
      marketing: { name: "Publicité & marketing", desc: "Traceurs de campagnes (Google Ads, VWO, Intercom, etc.). Utilisés pour mesurer l'efficacité de nos actions et personnaliser certaines communications." },
    },
  },
  en: {
    title: "We respect your privacy",
    intro:
      "By default we only use strictly necessary cookies. With your consent we may measure site audience and serve relevant advertising. You can change your choices at any time.",
    learnMore: "Read the privacy policy",
    accept: "Accept all",
    reject: "Reject all",
    customize: "Customize",
    savePrefs: "Save my choices",
    close: "Close",
    cats: {
      necessary: { name: "Strictly necessary", desc: "Security (Cloudflare), session and authentication. Essential for the site to work.", locked: "Always active" },
      analytics: { name: "Audience measurement", desc: "Google Analytics and PostHog. Help us understand how the site is used so we can improve it." },
      marketing: { name: "Advertising & marketing", desc: "Campaign trackers (Google Ads, VWO, Intercom, etc.). Used to measure ad effectiveness and personalise some communications." },
    },
  },
};

export default function CookieConsent() {
  const { lang } = useLanguage();
  const l = T[lang] || T.fr;
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  // On mount: apply saved consent OR show the banner (opt-in — nothing loads before choice).
  useEffect(() => {
    const saved = readConsent();
    if (saved && saved.date) {
      setPrefs({ analytics: !!saved.analytics, marketing: !!saved.marketing });
      applyConsent(saved);
    } else {
      setVisible(true);
    }
    const onOpen = () => {
      const saved = readConsent();
      if (saved) setPrefs({ analytics: !!saved.analytics, marketing: !!saved.marketing });
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener("y5:consent-open", onOpen);
    return () => window.removeEventListener("y5:consent-open", onOpen);
  }, []);

  const persist = (next) => {
    const saved = saveConsent(next);
    applyConsent(saved);
    setVisible(false);
    setExpanded(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  if (!visible) return null;

  const btnBase = "w-full sm:w-auto px-6 py-3 text-sm font-semibold transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-y5-yellow";
  const btnPrimary = "bg-y5-yellow text-y5-ink";
  const btnGhost = "border border-white/30 text-white hover:border-y5-yellow hover:text-y5-yellow";

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-4 md:p-6" role="dialog" aria-modal="false" aria-label={l.title} data-testid="cookie-consent">
      <div className="max-w-4xl mx-auto bg-y5-surface border border-y5-line shadow-2xl backdrop-blur-sm">
        <div className="p-6 md:p-7">
          <div className="flex items-start gap-4 mb-4">
            <span className="text-y5-yellow shrink-0 mt-1"><Cookie size={22} /></span>
            <div className="flex-1">
              <h3 className="font-display text-lg md:text-xl uppercase tracking-tight text-white">{l.title}</h3>
              <p className="text-y5-muted text-sm leading-relaxed mt-2">{l.intro}</p>
              <Link to="/politique-confidentialite" onClick={() => setVisible(false)} className="inline-block text-y5-yellow text-sm mt-2 underline underline-offset-2 hover:opacity-80" data-testid="consent-privacy-link">
                {l.learnMore}
              </Link>
            </div>
            {readConsent() && (
              <button onClick={() => setVisible(false)} aria-label={l.close} className="text-white/50 hover:text-white shrink-0" data-testid="consent-close">
                <X size={18} />
              </button>
            )}
          </div>

          {expanded && (
            <div className="border-t border-y5-line mt-4 pt-4 space-y-3" data-testid="consent-categories">
              <CategoryRow name={l.cats.necessary.name} desc={l.cats.necessary.desc} locked={l.cats.necessary.locked} enabled disabled />
              <CategoryRow
                name={l.cats.analytics.name}
                desc={l.cats.analytics.desc}
                enabled={prefs.analytics}
                onToggle={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                testId="toggle-analytics"
              />
              <CategoryRow
                name={l.cats.marketing.name}
                desc={l.cats.marketing.desc}
                enabled={prefs.marketing}
                onToggle={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                testId="toggle-marketing"
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-5">
            {expanded ? (
              <button onClick={savePrefs} className={`${btnBase} ${btnPrimary}`} data-testid="consent-save">{l.savePrefs}</button>
            ) : (
              <>
                <button onClick={rejectAll} className={`${btnBase} ${btnGhost}`} data-testid="consent-reject">{l.reject}</button>
                <button onClick={() => setExpanded(true)} className={`${btnBase} ${btnGhost}`} data-testid="consent-customize">{l.customize}</button>
                <button onClick={acceptAll} className={`${btnBase} ${btnPrimary}`} data-testid="consent-accept">{l.accept}</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ name, desc, locked, enabled, disabled = false, onToggle, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{name}</p>
        <p className="text-y5-muted text-xs mt-1 leading-relaxed">{desc}</p>
      </div>
      {disabled ? (
        <span className="shrink-0 inline-flex items-center gap-1 text-y5-yellow text-xs uppercase tracking-widest border border-y5-yellow/40 px-2 py-1">
          <Check size={12} /> {locked}
        </span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={onToggle}
          data-testid={testId}
          className={`shrink-0 relative inline-flex h-6 w-11 items-center transition-colors ${enabled ? "bg-y5-yellow" : "bg-white/15"}`}
        >
          <span className={`inline-block h-4 w-4 transform bg-y5-ink transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      )}
    </div>
  );
}
